import { useEffect } from 'react'
import { makeAutoObservable, runInAction } from 'mobx'
import {
    APICreateQueueTaskRequestBody,
    type APIQueueTask,
    RegularQueueTaskRequestBody,
    ChainedQueueTasksRequestBody,
    APIChainedQueueTask,
    APICreateQueueTaskResponseBody,
} from '@visorpro/client'
import _ from 'lodash'

import { visorPROPipelineClient } from '../util/api'
import type { APIGetQueueTaskRequestFilteringParams } from '@visorpro/client/dist/PipelineClient/services'
import { observed } from '../util/observed-decorator'

export class QueueTasksStore {
    public tasks: Record<string, APIQueueTask> = {}
    /**
     * Tasks fetched on their own, which unlike listed ones come with their
     * descendants. Kept from `get` until `forget`.
     */
    public details: Record<string, APIQueueTask> = {}
    public params?: APIGetQueueTaskRequestFilteringParams = undefined
    public totalTasks: number = 0
    public isFetched = false
    public isFetching = false
    public isCreating = false

    private detailIds = new Set<string>()

    constructor() {
        makeAutoObservable(this)
    }

    /** The listed tasks, each as fetched on its own once it has been. */
    public get listedTasks() {
        return _.values(this.tasks).map((task) => this.details[task.id] ?? task)
    }

    public async get(id: string) {
        this.detailIds.add(id)
        const response = await visorPROPipelineClient.tasks.get(id)
        runInAction(() => {
            const current = this.details[id]
            // The task may have been forgotten meanwhile, or a later fetch
            // may have come back first.
            if (
                this.detailIds.has(id) &&
                (!current ||
                    response.updated_at.toMillis() >=
                        current.updated_at.toMillis())
            ) {
                this.details[id] = response
            }
        })
    }

    public forget(id: string) {
        this.detailIds.delete(id)
        delete this.details[id]
    }

    @observed('isFetching')
    public async list(params?: APIGetQueueTaskRequestFilteringParams) {
        if (!_.isEqual(params, this.params)) {
            runInAction(() => {
                this.isFetched = false
            })
        }

        const response = await visorPROPipelineClient.tasks.list(params)
        runInAction(() => {
            this.tasks = _.keyBy(response.items, 'id')
            this.totalTasks = response.total
            this.isFetched = true
            this.params = params
        })
    }

    async create(body: RegularQueueTaskRequestBody): Promise<APIQueueTask>
    async create(body: ChainedQueueTasksRequestBody): Promise<APIChainedQueueTask>
    async create(body: RegularQueueTaskRequestBody[]): Promise<APIQueueTask[]>
    async create(
        body: ChainedQueueTasksRequestBody[],
    ): Promise<APIChainedQueueTask[]>
    async create(
        body: (RegularQueueTaskRequestBody | ChainedQueueTasksRequestBody)[],
    ): Promise<(APIQueueTask | APIChainedQueueTask)[]>
    @observed('isCreating')
    async create(
        body: APICreateQueueTaskRequestBody,
    ): Promise<APICreateQueueTaskResponseBody> {
        if (Array.isArray(body)) {
            return await visorPROPipelineClient.tasks.create(body)
        } else if ('chain' in body) {
            return await visorPROPipelineClient.tasks.create(body)
        } else {
            return await visorPROPipelineClient.tasks.create(body)
        }
    }
}

export const useQueueTasks = (
    store: QueueTasksStore,
    params?: APIGetQueueTaskRequestFilteringParams,
) => {
    useEffect(() => {
        void store.list(params)
    }, [params, store])

    return store.listedTasks
}
